// NOTIF-002 — absences & délégation. Hook CRUD partagé self ⇄ admin.
// `userId` null → self-service (/api/me/absences) ; sinon admin
// (/api/users/{userId}/absences, requiert `users:update`). Le composant appelant
// ne connaît que le mode via `userId`.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch, API_BASE } from '@/lib/api'
import { queryKeys } from '@/lib/queryKeys'

import { entityHeaders, jsonHeaders, readError } from './useNotifications.shared'
import type { UserBrief } from './useNotifications.types'

export type AbsenceStatus = 'SCHEDULED' | 'ACTIVE' | 'ENDED' | 'CANCELLED'

/** Override « délégué par entité » (Ph2) tel que renvoyé dans une Absence. */
export interface AbsenceDelegateOverride {
    entity_id: string
    entity_name: string
    delegate: UserBrief
}

export interface Absence {
    id: string
    user_id: string
    delegate: UserBrief
    delegates: AbsenceDelegateOverride[]
    starts_at: string
    ends_at: string
    status: AbsenceStatus
    migrate_open_notifications: boolean
    note: string | null
    is_current: boolean
    created_at: string
}

/** Override envoyé au backend : entité → délégué (id seul). */
export interface DelegateOverridePayload {
    entity_id: string
    delegate_id: string
}

export interface AbsenceCreatePayload {
    starts_at: string
    ends_at: string
    delegate_id: string
    delegates?: DelegateOverridePayload[]
    migrate_open_notifications?: boolean
    note?: string | null
}

export interface AbsenceUpdatePayload {
    starts_at?: string
    ends_at?: string
    delegate_id?: string
    delegates?: DelegateOverridePayload[]
    migrate_open_notifications?: boolean
    note?: string | null
}

export interface Colleague {
    id: string
    full_name: string | null
    email: string
    status: string
    /** Photo de profil (`users.avatar_url`) — repli automatique sur les initiales. */
    avatar_url: string | null
}

export interface DelegateCoverage {
    fully_covered: boolean
    missing_entities: { id: string; name: string }[]
    missing_resources: string[]
}

/** Collègues candidats à la délégation dans le scope courant (session-scopé : ne
 *  requiert PAS `users:read`, contrairement à `useUsers`). `userId` null → /me. */
export function useColleagues(userId: string | null, entityId: string | null) {
    const base = userId
        ? `${API_BASE}/users/${userId}/colleagues`
        : `${API_BASE}/me/colleagues`
    const query = useQuery<{ items: Colleague[] }>({
        queryKey: [...queryKeys.notifications.absences(userId ?? 'me'), 'colleagues'],
        enabled: Boolean(entityId),
        staleTime: 60_000,
        queryFn: async () => {
            const resp = await apiFetch(base, { headers: entityHeaders(entityId as string) })
            if (!resp.ok) throw await readError(resp)
            return resp.json()
        },
    })
    return { colleagues: query.data?.items ?? [], isLoading: query.isLoading }
}

/** Écart de couverture d'un délégué candidat vs l'absent (avertissement non bloquant).
 *  `overrideEntityId` fourni → couverture d'UNE entité (override par entité, Ph2). */
export function useDelegateCoverage(
    userId: string | null,
    delegateId: string,
    entityId: string | null,
    overrideEntityId?: string,
) {
    const base = userId
        ? `${API_BASE}/users/${userId}/absences/delegate-coverage`
        : `${API_BASE}/me/absences/delegate-coverage`
    return useQuery<DelegateCoverage>({
        queryKey: [
            ...queryKeys.notifications.absences(userId ?? 'me'),
            'coverage',
            delegateId,
            overrideEntityId ?? 'default',
        ],
        enabled: Boolean(entityId) && Boolean(delegateId),
        staleTime: 60_000,
        queryFn: async () => {
            const params = new URLSearchParams({ delegate_id: delegateId })
            if (overrideEntityId) params.set('entity_id', overrideEntityId)
            const resp = await apiFetch(`${base}?${params.toString()}`, {
                headers: entityHeaders(entityId as string),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json()
        },
    })
}

export function useAbsences(userId: string | null, entityId: string | null) {
    const qc = useQueryClient()
    const base = userId ? `${API_BASE}/users/${userId}/absences` : `${API_BASE}/me/absences`
    const scope = userId ?? 'me'
    const key = queryKeys.notifications.absences(scope)

    const query = useQuery<{ items: Absence[] }>({
        queryKey: key,
        enabled: Boolean(entityId),
        staleTime: 30_000,
        queryFn: async () => {
            const resp = await apiFetch(base, { headers: entityHeaders(entityId as string) })
            if (!resp.ok) throw await readError(resp)
            return resp.json()
        },
    })

    const invalidate = () => qc.invalidateQueries({ queryKey: key })

    const create = useMutation({
        mutationFn: async (body: AbsenceCreatePayload) => {
            const resp = await apiFetch(base, {
                method: 'POST',
                headers: jsonHeaders(entityId as string),
                body: JSON.stringify(body),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json() as Promise<Absence>
        },
        onSuccess: invalidate,
    })

    const update = useMutation({
        mutationFn: async ({ id, body }: { id: string; body: AbsenceUpdatePayload }) => {
            const resp = await apiFetch(`${base}/${id}`, {
                method: 'PATCH',
                headers: jsonHeaders(entityId as string),
                body: JSON.stringify(body),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json() as Promise<Absence>
        },
        onSuccess: invalidate,
    })

    const cancel = useMutation({
        mutationFn: async (id: string) => {
            const resp = await apiFetch(`${base}/${id}`, {
                method: 'DELETE',
                headers: entityHeaders(entityId as string),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json()
        },
        onSuccess: invalidate,
    })

    return {
        absences: query.data?.items ?? [],
        isLoading: query.isLoading,
        create,
        update,
        cancel,
    }
}
